import { useState, useMemo, useCallback } from "react";
import {
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Checkbox,
  Divider,
  Loader,
  Tooltip,
} from "@mantine/core";
import { IconFlask, IconCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useToggleSlideDone } from "../../../hooks/useStainRequest";

interface StainsWidgetProps {
  caseId: string | null;
  caseData: any;
  onRequestStains: () => void;
  isReadOnly?: boolean;
}

interface SlideEntry {
  slideId: string;
  slideNumber: string;
  blockNumber: string;
  stainCode: string;
  stainType: string;
  isCompleted: boolean;
  isAdditional: boolean;
  origin: string;
}

interface SpecimenSection {
  specimenId: string;
  specimenNumber: string;
  sourceName: string;
  subsite: string | null;
  blocks: {
    blockId: string;
    blockNumber: string;
    slides: SlideEntry[];
  }[];
}

function buildSpecimenSections(caseData: any): SpecimenSection[] {
  if (!caseData?.specimens) return [];

  return [...caseData.specimens]
    .sort((a: any, b: any) => (a.number || "").localeCompare(b.number || ""))
    .map((specimen: any) => {
      const blocks = [...(specimen.blocks || [])].sort((a: any, b: any) =>
        (a.number || "").localeCompare(b.number || "")
      );

      return {
        specimenId: specimen.id,
        specimenNumber: specimen.number || "?",
        sourceName: specimen.source?.name || "",
        subsite: specimen.subsite?.name || null,
        blocks: blocks.map((block: any) => ({
          blockId: block.id,
          blockNumber: block.number || "?",
          slides: [...(block.slides || [])]
            .sort((a: any, b: any) => {
              const cmp = (a.number || "").localeCompare(b.number || "");
              if (cmp !== 0) return cmp;
              return (a.createdAt || "").localeCompare(b.createdAt || "");
            })
            .map(
              (slide: any): SlideEntry => ({
                slideId: slide.id,
                slideNumber: slide.number || "?",
                blockNumber: block.number || "?",
                stainCode: slide.stain?.code || "H&E",
                stainType: slide.stain?.stainType || "",
                isCompleted: slide.isCompleted ?? false,
                isAdditional: slide.isAdditional ?? false,
                origin: slide.origin || "",
              })
            ),
        })),
      };
    })
    .filter((sp) => sp.blocks.some((b) => b.slides.length > 0));
}

export function StainsWidget({
  caseId,
  caseData,
  onRequestStains,
  isReadOnly,
}: StainsWidgetProps) {
  const [togglingSlides, setTogglingSlides] = useState<Record<string, boolean>>(
    {}
  );

  const toggleDoneMutation = useToggleSlideDone(caseId || undefined);

  const handleToggleDone = useCallback(
    async (slideId: string, currentDone: boolean) => {
      setTogglingSlides((prev) => ({ ...prev, [slideId]: true }));
      try {
        await toggleDoneMutation.mutateAsync(slideId);
      } catch {
        notifications.show({
          title: "Error",
          message: `Failed to mark slide as ${
            currentDone ? "not done" : "done"
          }`,
          color: "red",
        });
      } finally {
        setTogglingSlides((prev) => {
          const next = { ...prev };
          delete next[slideId];
          return next;
        });
      }
    },
    [toggleDoneMutation]
  );

  const handleToggleDoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const slideId = event.currentTarget.dataset.slideId;
      if (!slideId) return;
      void handleToggleDone(
        slideId,
        event.currentTarget.dataset.currentDone === "true"
      );
    },
    [handleToggleDone]
  );

  const sections = useMemo(() => buildSpecimenSections(caseData), [caseData]);

  const canRequestAdditionalStains = useMemo(
    () =>
      !caseData?.isBlocked &&
      (caseData?.specimens?.some((s: any) => s.blocks && s.blocks.length > 0) ??
        false),
    [caseData]
  );

  const canCompletePendingStainsAfterSignOff = useMemo(
    () => Boolean(caseData?.isFinalized && !caseData?.isBlocked),
    [caseData?.isBlocked, caseData?.isFinalized]
  );

  if (sections.length === 0) {
    return (
      <Stack gap="xs">
        <Text size="sm" c="dimmed">
          No slides recorded
        </Text>
        {canRequestAdditionalStains && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconFlask size={13} />}
            onClick={onRequestStains}
          >
            Request Stains
          </Button>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={0}>
      {sections.map((sp, spIdx) => {
        const totalSlides = sp.blocks.reduce(
          (sum, b) => sum + b.slides.length,
          0
        );
        const doneSlides = sp.blocks.reduce(
          (sum, b) => sum + b.slides.filter((s) => s.isCompleted).length,
          0
        );
        const allDone = doneSlides === totalSlides && totalSlides > 0;

        return (
          <Stack key={sp.specimenId} gap={0}>
            {spIdx > 0 && <Divider my="xs" />}

            {/* Specimen header */}
            <Group gap="xs" wrap="nowrap" mb={6}>
              <Badge
                size="sm"
                variant="dot"
                color={allDone ? "green" : "orange"}
                style={{ flexShrink: 0 }}
              >
                {sp.specimenNumber}
              </Badge>
              <Text
                size="xs"
                fw={600}
                style={{ flex: 1, minWidth: 0 }}
                truncate
              >
                {sp.sourceName}
                {sp.subsite ? `, ${sp.subsite}` : ""}
              </Text>
              <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                {doneSlides}/{totalSlides}
              </Text>
            </Group>

            {/* Blocks and stains */}
            {sp.blocks.map((block) => (
              <Stack key={block.blockId} gap={2} mb={4}>
                <Text size="xs" c="dimmed" pl={4} fw={500}>
                  Block {block.blockNumber}
                </Text>
                {block.slides.map((slide, slideIdx) => {
                  const isToggling = !!togglingSlides[slide.slideId];
                  return (
                    <Group
                      key={slide.slideId}
                      gap="xs"
                      wrap="nowrap"
                      pl={8}
                      py={2}
                      style={{
                        background: slide.isCompleted
                          ? "var(--mantine-color-green-0)"
                          : undefined,
                        borderRadius: 4,
                      }}
                    >
                      <Text
                        size="xs"
                        c="dimmed"
                        style={{ flexShrink: 0, minWidth: 16 }}
                      >
                        {slideIdx + 1}
                      </Text>
                      <Text size="xs" fw={500} style={{ flex: 1 }}>
                        {slide.stainCode}
                        {slide.stainType === "IHC" && (
                          <Badge
                            size="xs"
                            variant="light"
                            color="violet"
                            ml={4}
                          >
                            IHC
                          </Badge>
                        )}
                        {slide.isAdditional && (
                          <Badge size="xs" variant="light" color="blue" ml={4}>
                            Add'l
                          </Badge>
                        )}
                        {slide.origin === "sending_client" && (
                          <Tooltip
                            label="Received already stained from the referring lab — no in-house staining performed."
                            withArrow
                            multiline
                            maw={240}
                          >
                            <Badge
                              size="xs"
                              variant="light"
                              color="cyan"
                              ml={4}
                            >
                              Pre-stained
                            </Badge>
                          </Tooltip>
                        )}
                      </Text>
                      {isToggling ? (
                        <Loader size="xs" style={{ flexShrink: 0 }} />
                      ) : (
                        <Checkbox
                          size="xs"
                          checked={slide.isCompleted}
                          data-slide-id={slide.slideId}
                          data-current-done={slide.isCompleted}
                          onChange={handleToggleDoneChange}
                          disabled={
                            isReadOnly &&
                            (!canCompletePendingStainsAfterSignOff ||
                              slide.isCompleted)
                          }
                          color="green"
                          icon={({ ...props }) => <IconCheck {...props} />}
                          style={{ flexShrink: 0 }}
                        />
                      )}
                    </Group>
                  );
                })}
              </Stack>
            ))}
          </Stack>
        );
      })}

      {canRequestAdditionalStains && (
        <Button
          size="xs"
          variant="light"
          leftSection={<IconFlask size={13} />}
          onClick={onRequestStains}
          mt="xs"
        >
          Request Stains
        </Button>
      )}
    </Stack>
  );
}
